import React, { useState, useEffect, useCallback } from 'react';
import { useGym } from '../contexts/GymContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { investmentAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Plus, TrendingUp, Building2, Trash2, Edit2, 
  DollarSign, PieChart, Target, Percent
} from 'lucide-react';
import { formatCurrency as formatCurrencyUtil, formatCurrencyCompact as formatCurrencyCompactUtil, formatPercent } from '../utils/formatters';

const ROIPage = () => {
  const { selectedGym } = useGym();
  const [investments, setInvestments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalData, setTotalData] = useState({ total: 0, by_category: {}, count: 0 });
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    description: '',
    date: ''
  });

  const currentYear = new Date().getFullYear();

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    setLoading(true);
    try {
      const [investmentsRes, categoriesRes, totalRes, roiRes] = await Promise.all([
        investmentAPI.getAll(selectedGym.id),
        investmentAPI.getCategories(selectedGym.id),
        investmentAPI.getTotal(selectedGym.id),
        investmentAPI.getMonthlyROI(selectedGym.id, currentYear)
      ]);
      setInvestments(investmentsRes.data);
      setCategories(categoriesRes.data);
      setTotalData(totalRes.data);
      setRoiData(roiRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [selectedGym, currentYear]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
    }
  }, [selectedGym, loadData]);

  const resetForm = () => {
    setFormData({ category: '', amount: '', description: '', date: '' });
    setEditingInvestment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        gym_id: selectedGym.id,
        category: formData.category,
        amount: parseFloat(formData.amount),
        description: formData.description || null,
        date: formData.date || null
      };

      if (editingInvestment) {
        await investmentAPI.update(editingInvestment.id, data);
        toast.success('Investimento atualizado!');
      } else {
        await investmentAPI.create(data);
        toast.success('Investimento adicionado!');
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar investimento');
    }
  };

  const handleEdit = (investment) => {
    setEditingInvestment(investment);
    setFormData({
      category: investment.category,
      amount: investment.amount.toString(),
      description: investment.description || '',
      date: investment.date || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este investimento?')) return;
    try {
      await investmentAPI.delete(id);
      toast.success('Investimento excluído!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir investimento');
    }
  };

  // Use the centralized formatters
  const formatCurrency = formatCurrencyUtil;
  const formatCurrencyCompact = formatCurrencyCompactUtil;

  // Get current month ROI
  const currentMonth = new Date().getMonth();
  const currentROI = roiData?.monthly_roi?.[currentMonth]?.roi || 0;

  // Group investments by category
  const investmentsByCategory = investments.reduce((acc, inv) => {
    if (!acc[inv.category]) {
      acc[inv.category] = [];
    }
    acc[inv.category].push(inv);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header com saudação e filtros */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              ROI - Investimentos
            </h1>
            <p className="text-zinc-400 mt-2 text-base">Gerencie os investimentos e acompanhe o retorno</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-investment-button" className="bg-emerald-600 hover:bg-emerald-700 h-10">
                <Plus size={20} className="mr-2" />
                Adicionar
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-emerald-400">
                  {editingInvestment ? 'Editar Investimento' : 'Novo Investimento'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Categoria do Investimento</Label>
                  <select
                    data-testid="investment-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="input-field w-full mt-1"
                    required
                  >
                    <option value="">Selecione uma categoria</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-zinc-500 mt-1">
                    Gerencie categorias em Configurações
                  </p>
                </div>
                <div>
                  <Label className="text-zinc-300">Valor (R$)</Label>
                  <Input
                    data-testid="investment-amount-input"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="input-field mt-1"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Data (opcional)</Label>
                  <Input
                    data-testid="investment-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input-field mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Descrição (opcional)</Label>
                  <Input
                    data-testid="investment-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-field mt-1"
                    placeholder="Detalhes do investimento"
                  />
                </div>
                <Button data-testid="save-investment-button" type="submit" className="w-full btn-primary">
                  {editingInvestment ? 'Atualizar' : 'Salvar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Investment */}
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-[#D4A43A]/20 flex-shrink-0">
                  <Building2 className="text-[#D4A43A]" size={24} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Investimento Total</p>
                  <p className="text-base sm:text-lg lg:text-xl font-bold text-[#D4A43A] truncate">
                    {formatCurrencyCompact(totalData.total)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ROI Current */}
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${currentROI >= 0 ? 'bg-[#22C55E]/20' : 'bg-[#EF4444]/20'}`}>
                  <Percent className={currentROI >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'} size={24} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">ROI Atual</p>
                  <p className={`text-2xl font-bold ${currentROI >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                    {currentROI.toFixed(1)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profit Accumulated */}
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-[#22C55E]/20 flex-shrink-0">
                  <TrendingUp className="text-[#22C55E]" size={24} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Lucro Acumulado</p>
                  <p className="text-base sm:text-lg lg:text-xl font-bold text-[#22C55E] truncate">
                    {formatCurrencyCompact(roiData?.monthly_roi?.[currentMonth]?.cumulative_profit || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Categories Count */}
          <Card className="glass-panel border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-[#10B981]/20">
                  <PieChart className="text-[#10B981]" size={24} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Categorias</p>
                  <p className="text-2xl font-bold text-[#10B981]">
                    {Object.keys(totalData.by_category).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ROI Monthly Chart */}
        {roiData && totalData.total > 0 && (
          <Card className="glass-panel border-white/10">
            <CardHeader>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Target className="text-[#D4A43A]" />
                ROI Mensal - {currentYear}
              </CardTitle>
              <p className="text-sm text-zinc-500">
                Fórmula: ROI = (Lucro Acumulado / Investimento Total) × 100
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
                {roiData.monthly_roi.map((month, index) => {
                  const isCurrentMonth = index === currentMonth;
                  const hasProfitData = month.revenue > 0 || month.expenses > 0;
                  return (
                    <div 
                      key={month.month}
                      className={`text-center p-3 rounded-lg transition-all ${
                        isCurrentMonth 
                          ? 'bg-[#D4A43A]/20 border border-[#D4A43A]/50' 
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <p className="text-xs text-zinc-500 uppercase">{month.month_short}</p>
                      <p className={`text-lg font-bold ${
                        !hasProfitData 
                          ? 'text-zinc-600' 
                          : month.roi >= 0 
                            ? 'text-[#22C55E]' 
                            : 'text-[#EF4444]'
                      }`}>
                        {hasProfitData ? `${month.roi.toFixed(0)}%` : '-'}
                      </p>
                      {hasProfitData && (
                        <p className="text-[10px] text-zinc-500">
                          {formatCurrencyCompact(month.profit).replace('R$', '')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Investment by Category */}
        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-xl text-white">Investimentos por Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(investmentsByCategory).length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-500">Nenhum investimento cadastrado</p>
                <p className="text-sm text-zinc-600 mt-2">
                  Adicione seus investimentos para calcular o ROI
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(investmentsByCategory).map(([category, items]) => {
                  const categoryTotal = items.reduce((sum, item) => sum + item.amount, 0);
                  const percentage = totalData.total > 0 ? (categoryTotal / totalData.total * 100) : 0;
                  
                  return (
                    <div key={category} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-[#D4A43A]/20">
                            <Building2 className="text-[#D4A43A]" size={18} />
                          </div>
                          <div>
                            <h3 className="text-white font-semibold">{category}</h3>
                            <p className="text-xs text-zinc-500">{items.length} item(ns)</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#D4A43A]">
                            {formatCurrencyCompact(categoryTotal)}
                          </p>
                          <p className="text-xs text-zinc-500">{percentage.toFixed(1)}% do total</p>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#D4A43A] to-[#22C55E] transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>

                      {/* Items */}
                      <div className="space-y-2 pl-4 border-l-2 border-zinc-800">
                        {items.map(item => (
                          <div 
                            key={item.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                          >
                            <div>
                              <p className="text-zinc-300">{item.description || 'Sem descrição'}</p>
                              {item.date && (
                                <p className="text-xs text-zinc-500">
                                  {new Date(item.date).toLocaleDateString('pt-BR')}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="text-[#D4A43A] font-semibold">
                                {formatCurrencyCompact(item.amount)}
                              </p>
                              <button
                                onClick={() => handleEdit(item)}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                              >
                                <Edit2 size={16} className="text-zinc-400" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                              >
                                <Trash2 size={16} className="text-[#EF4444]" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ROIPage;
